import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  IconButton, Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { useCobranzaStore } from '../store/cobranzaStore';
import type { CobranzaCreateRequest, CobranzaDetalleRequest } from '../types/cobranza.types';

export const CobranzaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id && id !== 'nueva';
  const { selectedCobranza, estadoCuenta, loading, fetchCobranzaById, saveCobranza, fetchEstadoCuenta } = useCobranzaStore();

  const [formData, setFormData] = useState<CobranzaCreateRequest>({ idPersona: 0, detalles: [] });

  useEffect(() => { if (isEdit && id) fetchCobranzaById(parseInt(id)); }, [id]);
  useEffect(() => {
    if (isEdit && selectedCobranza) {
      setFormData({
        idPersona: selectedCobranza.idPersona || 0,
        idFormaPago: selectedCobranza.idFormaPago,
        idBanco: selectedCobranza.idBanco,
        idCuentaBancaria: selectedCobranza.idCuentaBancaria,
        numero: selectedCobranza.numero,
        observaciones: selectedCobranza.observaciones,
        detalles: selectedCobranza.detalles?.map((d) => ({
          idFactura: d.idFactura || 0,
          montoAplicado: d.montoAplicado,
        })) || [],
      });
    }
  }, [selectedCobranza]);

  const handleChange = (field: keyof CobranzaCreateRequest, value: any) => setFormData((prev) => ({ ...prev, [field]: value }));

  const handleDetalleChange = (index: number, field: keyof CobranzaDetalleRequest, value: any) => {
    setFormData((prev) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () => setFormData((prev) => ({ ...prev, detalles: [...prev.detalles, { idFactura: 0, montoAplicado: 0 }] }));
  const removeDetalle = (index: number) => setFormData((prev) => ({ ...prev, detalles: prev.detalles.filter((_, i) => i !== index) }));

  const totalCobro = useMemo(() => formData.detalles.reduce((sum, d) => sum + (d.montoAplicado || 0), 0), [formData.detalles]);

  const handleVerEstadoCuenta = () => { if (formData.idPersona) fetchEstadoCuenta(formData.idPersona); };

  const handleSubmit = async () => {
    try { await saveCobranza(formData); navigate('/ventas/cobranza'); } catch { /* handled */ }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ventas/cobranza')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">{isEdit ? 'Editar Cobro' : 'Nuevo Cobro'}</Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos del Cobro</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="ID Cliente" size="small" fullWidth type="number" value={formData.idPersona || ''} onChange={(e) => handleChange('idPersona', parseInt(e.target.value) || 0)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Button variant="outlined" startIcon={<AccountBalanceIcon />} onClick={handleVerEstadoCuenta} disabled={!formData.idPersona} fullWidth sx={{ height: 40 }}>
              Ver Estado de Cuenta
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="ID Forma Pago" size="small" fullWidth type="number" value={formData.idFormaPago || ''} onChange={(e) => handleChange('idFormaPago', parseInt(e.target.value) || null)} /></Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}><TextField label="Numero" size="small" fullWidth value={formData.numero || ''} onChange={(e) => handleChange('numero', e.target.value)} /></Grid>
          <Grid size={{ xs: 12 }}><TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} /></Grid>
        </Grid>
      </Paper>

      {estadoCuenta && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: 'info.50' }}>
          <Typography variant="h6" gutterBottom>Estado de Cuenta - {estadoCuenta.razonSocial}</Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 4 }}><Typography variant="body2" color="text.secondary">Total Facturado</Typography><Typography variant="h6">S/ {estadoCuenta.totalFacturado.toFixed(2)}</Typography></Grid>
            <Grid size={{ xs: 4 }}><Typography variant="body2" color="text.secondary">Total Cobrado</Typography><Typography variant="h6" color="success.main">S/ {estadoCuenta.totalCobrado.toFixed(2)}</Typography></Grid>
            <Grid size={{ xs: 4 }}><Typography variant="body2" color="text.secondary">Saldo Pendiente</Typography><Typography variant="h6" color="error.main">S/ {estadoCuenta.saldoPendiente.toFixed(2)}</Typography></Grid>
          </Grid>
        </Paper>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Aplicacion de Cobro</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Documento</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead><TableRow><TableCell width={50}>#</TableCell><TableCell>ID Factura</TableCell><TableCell width={150}>Monto Aplicado</TableCell><TableCell width={50}></TableCell></TableRow></TableHead>
            <TableBody>
              {formData.detalles.map((det, idx) => (
                <TableRow key={idx}>
                  <TableCell>{idx + 1}</TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.idFactura || ''} onChange={(e) => handleDetalleChange(idx, 'idFactura', parseInt(e.target.value) || 0)} /></TableCell>
                  <TableCell><TextField size="small" type="number" fullWidth value={det.montoAplicado || 0} onChange={(e) => handleDetalleChange(idx, 'montoAplicado', parseFloat(e.target.value) || 0)} /></TableCell>
                  <TableCell><IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton></TableCell>
                </TableRow>
              ))}
              {formData.detalles.length === 0 && <TableRow><TableCell colSpan={4} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin documentos aplicados</TableCell></TableRow>}
            </TableBody>
          </Table>
        </TableContainer>
        <Divider sx={{ my: 2 }} />
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Typography variant="h6">Total Cobro: S/ {totalCobro.toFixed(2)}</Typography>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/ventas/cobranza')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={loading}>{loading ? 'Guardando...' : 'Guardar Cobro'}</Button>
      </Box>
    </Box>
  );
};
